// This file is auto-generated by @hey-api/openapi-ts

import {
  createClient,
  createConfig,
  type Options,
} from "@hey-api/client-fetch";
import type {
  CreateTranslationJobData,
  CreateTranslationJobError,
  CreateTranslationJobResponse,
  GetDocumentAiAnalyzeIdResultsData,
  GetDocumentAiAnalyzeIdResultsError,
  GetDocumentAiAnalyzeIdResultsResponse,
  GetDocumentAiAnalyzeIdStatusData,
  GetDocumentAiAnalyzeIdStatusError,
  GetDocumentAiAnalyzeIdStatusResponse,
  GetDocumentAiResultsData,
  GetDocumentAiResultsError,
  GetDocumentAiResultsResponse,
  GetDocumentAiStatusData,
  GetDocumentAiStatusError,
  GetDocumentAiStatusResponse,
  GetTranslationJobData,
  GetTranslationJobError,
  GetTranslationJobResponse,
  PolishMessageData,
  PolishMessageError,
  PolishMessageResponse,
  ReprocessDocumentAiData,
  ReprocessDocumentAiError,
  ReprocessDocumentAiResponse,
  TranslateMessageData,
  TranslateMessageError,
  TranslateMessageResponse,
} from "./types.gen";

export const client = createClient(createConfig());

/**
 * Get the AI processing status for a specific document.
 * Get the AI processing status for a specific document.
 *
 * Authorization:
 * 1. JWT authentication via Auth0Bearer (API-level)
 * 2. Cedar permission check via @authorization_gate('document-ai:read')
 * 3. Document-level access enforced by Hasura delegation in get_accessible_by_user()
 *
 * Args:
 * document_id: UUID of the DocumentMetadata record
 *
 * Returns:
 * JSON response with processing status and metadata
 */
export const getDocumentAiStatus = <ThrowOnError extends boolean = false>(
  options: Options<GetDocumentAiStatusData, ThrowOnError>
) => {
  return (options?.client ?? client).get<
    GetDocumentAiStatusResponse,
    GetDocumentAiStatusError,
    ThrowOnError
  >({
    ...options,
    url: "/as/api/v1/admin/avela-ai/documents/{document_id}/status",
  });
};

/**
 * Get the AI processing results for a specific document.
 * Get the AI processing results for a specific document.
 *
 * Authorization:
 * 1. JWT authentication via Auth0Bearer (API-level)
 * 2. Cedar permission check via @authorization_gate('document-ai:read')
 * 3. Document-level access enforced by Hasura delegation in get_accessible_by_user().
 *
 * Only returns results for COMPLETED documents.
 *
 * Args:
 * document_id: UUID of the DocumentMetadata record
 *
 * Returns:
 * JSON response with OCR and LLM validation results
 */
export const getDocumentAiResults = <ThrowOnError extends boolean = false>(
  options: Options<GetDocumentAiResultsData, ThrowOnError>
) => {
  return (options?.client ?? client).get<
    GetDocumentAiResultsResponse,
    GetDocumentAiResultsError,
    ThrowOnError
  >({
    ...options,
    url: "/as/api/v1/admin/avela-ai/documents/{document_id}/results",
  });
};

/**
 * Trigger LLM reprocessing for a document using existing OCR results.
 * Trigger LLM reprocessing for a document using existing OCR results.
 *
 * Authorization:
 * 1. JWT authentication via Auth0Bearer (API-level)
 * 2. Cedar permission check via @authorization_gate('document-ai:update')
 * 3. Document-level access enforced by Hasura delegation in get_accessible_by_user().
 *
 * Reuses existing OCR results - only reprocesses LLM validation step.
 *
 * Args:
 * document_id: UUID of the DocumentMetadata record
 *
 * Returns:
 * JSON response with processing status
 *
 * Raises:
 * HttpError: If document not found, access denied, or no OCR results exist
 */
export const reprocessDocumentAi = <ThrowOnError extends boolean = false>(
  options: Options<ReprocessDocumentAiData, ThrowOnError>
) => {
  return (options?.client ?? client).post<
    ReprocessDocumentAiResponse,
    ReprocessDocumentAiError,
    ThrowOnError
  >({
    ...options,
    url: "/as/api/v1/admin/avela-ai/documents/{document_id}/reprocess",
  });
};

/**
 * Get the AnalyzeID processing status for a specific document.
 * Get the AnalyzeID processing status for a specific document.
 *
 * Authorization:
 * 1. JWT authentication via Auth0Bearer (API-level)
 * 2. Cedar permission check via @authorization_gate('document-ai:read')
 * 3. Document-level access enforced by Hasura delegation in
 * ``DocumentAI.identity.get_accessible_by_user()``
 *
 * Args:
 * document_id: UUID of the DocumentMetadata record
 *
 * Returns:
 * JSON response with processing status and metadata
 */
export const getDocumentAiAnalyzeIdStatus = <
  ThrowOnError extends boolean = false
>(
  options: Options<GetDocumentAiAnalyzeIdStatusData, ThrowOnError>
) => {
  return (options?.client ?? client).get<
    GetDocumentAiAnalyzeIdStatusResponse,
    GetDocumentAiAnalyzeIdStatusError,
    ThrowOnError
  >({
    ...options,
    url: "/as/api/v1/admin/avela-ai/documents/{document_id}/analyze-id/status",
  });
};

/**
 * Get the AnalyzeID processing results for a specific document.
 * Get the AnalyzeID processing results for a specific document.
 *
 * Authorization:
 * 1. JWT authentication via Auth0Bearer (API-level)
 * 2. Cedar permission check via @authorization_gate('document-ai:read')
 * 3. Document-level access enforced by Hasura delegation in
 * ``DocumentAI.identity.get_accessible_by_user()``
 *
 * When not ``COMPLETED``, includes a ``message``; when ``COMPLETED``, includes
 * ``processed_at`` and ``raw_results`` (AnalyzeID JSON results).
 *
 * Args:
 * document_id: UUID of the DocumentMetadata record
 *
 * Returns:
 * JSON response with AnalyzeID processing results
 */
export const getDocumentAiAnalyzeIdResults = <
  ThrowOnError extends boolean = false
>(
  options: Options<GetDocumentAiAnalyzeIdResultsData, ThrowOnError>
) => {
  return (options?.client ?? client).get<
    GetDocumentAiAnalyzeIdResultsResponse,
    GetDocumentAiAnalyzeIdResultsError,
    ThrowOnError
  >({
    ...options,
    url: "/as/api/v1/admin/avela-ai/documents/{document_id}/analyze-id/results",
  });
};

/**
 * @deprecated
 * Translate a message using LLMs
 * Translate a message subject, email HTML, and SMS text to target languages.
 *
 * Authorization:
 * 1. JWT authentication via Auth0Bearer (API-level)
 * 2. Cedar permission check via @authorization_gate('message:create')
 *
 * The endpoint accepts a message subject, email HTML content, and SMS text content,
 * translating all while preserving variable placeholders and message structure.
 *
 * Args:
 * payload: MessageTranslateRequest containing:
 * - subject: Message subject line to translate
 * - email: Email content as HTML string
 * - sms: SMS content as text string
 * - target_languages: Target language codes
 *
 * Returns:
 * MessageTranslateResponse containing:
 * - translations: A list of translated message objects, each with:
 * - subject: The translated subject line
 * - email: The translated email HTML content
 * - sms: The translated SMS text content
 * - translated_language: The language code used for the translation
 *
 * Error responses (normalized body: error, message, details?):
 * - 400 missing_content: At least one of subject, email, or sms must be provided.
 * - 400 invalid_target_languages: At least one target language is required.
 * - 401/403: From auth layer (JWT / Cedar).
 * - 422: From Ninja request validation.
 * - 500 translation_truncated / translation_parse_error / translation_failed.
 *
 * Raises:
 * MessageAPIError: All endpoint errors with stable codes and messages.
 */
export const translateMessage = <ThrowOnError extends boolean = false>(
  options: Options<TranslateMessageData, ThrowOnError>
) => {
  return (options?.client ?? client).post<
    TranslateMessageResponse,
    TranslateMessageError,
    ThrowOnError
  >({
    ...options,
    url: "/as/api/v1/admin/avela-ai/messages/translate",
  });
};

/**
 * Polish a message using LLMs
 * Polish a message subject, email HTML, and SMS text using LLMs.
 *
 * Authorization:
 * 1. JWT authentication via Auth0Bearer (API-level)
 * 2. Cedar permission check via @authorization_gate('message:create')
 *
 * The endpoint accepts a message subject, email HTML content, and SMS text
 * content, and attempts to improve the quality and clarity of the content
 * without changing the meaning of the message.
 *
 * Args:
 * payload: MessagePolishRequest containing:
 * - subject: Message subject line to polish
 * - email: Email content as HTML string
 * - sms: SMS content as text string
 *
 * Returns:
 * MessagePolishResponse containing:
 * - polished: A polished message object with:
 * - subject: The polished subject line
 * - email: The polished email HTML content
 * - sms: The polished SMS text content
 *
 * Error responses (normalized body: error, message, details?):
 * - 400 missing_content: At least one of subject, email, or sms must be provided.
 * - 401/403: From auth layer (JWT / Cedar).
 * - 422: From Ninja request validation (invalid MessagePolishRequest schema).
 * - 500 polish_truncated: Response truncated; reduce input or increase max_tokens.
 * - 500 polish_parse_error: Invalid JSON, wrong root type (not dict), or missing
 * required fields (subject, email, sms). See polish_service._parse_and_validate_polish.
 * - 500 polish_failed: Other service/network errors (Bedrock, timeout, etc.).
 *
 * Raises:
 * MessageAPIError: All endpoint errors with stable codes and messages.
 */
export const polishMessage = <ThrowOnError extends boolean = false>(
  options: Options<PolishMessageData, ThrowOnError>
) => {
  return (options?.client ?? client).post<
    PolishMessageResponse,
    PolishMessageError,
    ThrowOnError
  >({
    ...options,
    url: "/as/api/v1/admin/avela-ai/messages/polish",
  });
};

/**
 * Create an async translation job
 * Create an async translation job for all target languages.
 *
 * Creates a MessageAI record, stores the request payload, and
 * dispatches a Celery task. The frontend polls GET /translate/jobs/{id}
 * for results.
 *
 * Returns 202 with job_id and a retry_after_seconds hint.
 */
export const createTranslationJob = <ThrowOnError extends boolean = false>(
  options: Options<CreateTranslationJobData, ThrowOnError>
) => {
  return (options?.client ?? client).post<
    CreateTranslationJobResponse,
    CreateTranslationJobError,
    ThrowOnError
  >({
    ...options,
    url: "/as/api/v1/admin/avela-ai/messages/translate/jobs",
  });
};

/**
 * Get async translation job status and results
 * Get the status and results of an async translation job.
 *
 * Returns 404 for not-found OR for jobs belonging to another user
 * (avoids confirming job existence to unauthorized users).
 *
 * Translations are null until status is 'completed'.
 */
export const getTranslationJob = <ThrowOnError extends boolean = false>(
  options: Options<GetTranslationJobData, ThrowOnError>
) => {
  return (options?.client ?? client).get<
    GetTranslationJobResponse,
    GetTranslationJobError,
    ThrowOnError
  >({
    ...options,
    url: "/as/api/v1/admin/avela-ai/messages/translate/jobs/{job_id}",
  });
};
